// AprilTag / ArUco fiducial decoder, lazy-loaded against @techstark/opencv-js.
// Mirrors the (input, dictionary) → DecodeOutcome shape of qrDecoder.
//
// IMPORTANT:
//   • ROI fallback path stays QR-only — this decoder is not called from
//     the ROI center-tile branch in ContinuousScanPage. Fiducial budget
//     doesn't fit a second decode pass at native resolution.
//   • Storage key for the active-format picker differs between React
//     (`petf:scan:active-format`) and vanilla (`petf-vanilla-scan-
//     active-format`) — accepted prototype quirk; the two PWAs don't
//     share state.
//
// Plan: docs/plans/2026-06-29-001-feat-fiducial-active-format-picker-plan.md
// Design: docs/designs/2026-06-29-fiducial-detection-side-by-side-design.md

import type { ActiveFormat, BinBbox, DecodedSymbol, DecodedSymbolKind } from '../types';
import type { DecodeOutcome } from './qrDecoder';

/** Fiducial subset of ActiveFormat — explicit whitelist so newer non-fiducial
 * formats (e.g. 'text' for OCR) aren't accidentally routed here. */
export type FiducialFormat = 'aruco_4x4_1000';

export function isFiducialFormat(f: ActiveFormat): f is FiducialFormat {
  return f === 'aruco_4x4_1000';
}

export function fiducialStatusLabel(s: FiducialStatus): string {
  switch (s.state) {
    case 'idle':
      return 'opencv.js loads on first use (~4 MB gz)';
    case 'loading':
      return 'loading opencv.js…';
    case 'ready':
      return 'detector ready';
    case 'error':
      return `error: ${s.message}`;
  }
}

export function fiducialStatusColor(s: FiducialStatus): string {
  switch (s.state) {
    case 'ready':
      return '#d946ef'; // magenta — matches the ArUco bbox color
    case 'error':
      return '#f87171'; // red-400
    case 'loading':
    case 'idle':
    default:
      return '#9ca3af'; // gray-400
  }
}

// Promise-cached opencv runtime. Stays a single promise across calls so
// repeated decodes don't re-await the (potentially 1–2 s) WASM init. On
// failure the cached rejection is surfaced so the picker can auto-revert
// to 'qr' instead of spinning per-frame.
let cvPromise: Promise<OpenCvLike> | null = null;

/**
 * Current decoder status — read by the page to render a small status pill
 * next to the active-format picker (mobile users can't reach DevTools).
 * Updated as a side-effect of ensureOpenCv() and the first decodeFiducials()
 * call against a real frame.
 */
export type FiducialStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'error'; message: string };

let status: FiducialStatus = { state: 'idle' };
const statusListeners = new Set<(s: FiducialStatus) => void>();

export function subscribeFiducialStatus(listener: (s: FiducialStatus) => void): () => void {
  statusListeners.add(listener);
  listener(status);
  return () => {
    statusListeners.delete(listener);
  };
}

function setStatus(next: FiducialStatus): void {
  status = next;
  statusListeners.forEach((l) => l(next));
}

interface OpenCvLike {
  // The runtime surface we touch. Typed loosely because mirada's @types
  // don't cover the aruco/objdetect module in opencv.js 5.0 yet. Runtime
  // shape is documented in https://docs.opencv.org/5.0.0/d5/dae/tutorial_aruco_detection.html.
  Mat: new (...args: unknown[]) => unknown;
  matFromImageData: (imgData: ImageData) => unknown;
  // Aruco constants & classes are present on the global cv object in opencv.js 5.0
  // (objdetect module). We access them via index-signature lookups at decode time
  // so a missing entry surfaces as a runtime warning instead of a build break.
  [key: string]: unknown;
}

/**
 * Idempotent, promise-cached opencv.js init. Follows the dual-shape
 * pattern from @techstark/opencv-js's README: the default export is
 * either already a Promise, or an object exposing `Mat`/`onRuntimeInitialized`.
 */
export function ensureOpenCv(): Promise<OpenCvLike> {
  if (cvPromise) return cvPromise;
  setStatus({ state: 'loading' });
  cvPromise = (async () => {
    try {
      const mod = (await import('@techstark/opencv-js')).default as unknown;
      let cv: OpenCvLike;
      if (mod instanceof Promise) {
        cv = (await mod) as OpenCvLike;
      } else {
        const candidate = mod as OpenCvLike & { onRuntimeInitialized?: () => void };
        if (typeof candidate.Mat === 'function') {
          cv = candidate;
        } else {
          await new Promise<void>((resolve) => {
            candidate.onRuntimeInitialized = () => resolve();
          });
          cv = candidate;
        }
      }
      setStatus({ state: 'ready' });
      return cv;
    } catch (err) {
      setStatus({ state: 'error', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  })();
  return cvPromise;
}

/** Clear the cached opencv promise so the next decode attempt retries the load. */
export function resetOpenCvCache(): void {
  cvPromise = null;
}

/**
 * OpenCV PredefinedDictionaryType enum values. Used as fallback when the
 * named constant (e.g. `cv.DICT_4X4_1000`) isn't exposed on the runtime cv
 * object — the integer values are stable across OpenCV 4.x/5.0.
 * Source: modules/objdetect/include/opencv2/objdetect/aruco_dictionary.hpp
 *
 * Scope is QR + ArUco 4x4 (1000) per the active format union in types.ts.
 * To re-add AprilTag tag36h11 (20 / 'DICT_APRILTAG_36h11') or the larger
 * ArUco grids (DICT_5X5_100 = 5, DICT_6X6_250 = 10), widen ActiveFormat
 * in types.ts and add the corresponding entries here + a picker option.
 */
const FORMAT_DICT_INT: Record<FiducialFormat, number> = {
  aruco_4x4_1000: 3,
};

const FORMAT_CV_KEY: Record<FiducialFormat, string> = {
  aruco_4x4_1000: 'DICT_4X4_1000',
};

/**
 * Decode any fiducials of the given dictionary present in the source frame.
 * Returns the same DecodeOutcome shape as qrDecoder.decodeFrame so the
 * call-site dispatch can union both detectors behind one type.
 */
export async function decodeFiducials(
  imageData: ImageData,
  format: FiducialFormat
): Promise<DecodeOutcome> {
  let cv: OpenCvLike;
  try {
    cv = await ensureOpenCv();
  } catch (err) {
    console.warn('[fiducialDecoder] opencv.js load failed:', err);
    return { kind: 'none' };
  }

  // Emscripten flattens C++ namespaces to underscore-prefixed names. The
  // C++ class `cv::aruco::ArucoDetector` is exposed at runtime as
  // `cv.aruco_ArucoDetector`, NOT `cv.ArucoDetector` — the latter shows up
  // in the bundle as a string but isn't bound. Same convention applies to
  // `aruco_DetectorParameters` and `aruco_Dictionary`. The dictionary
  // constants and `getPredefinedDictionary` free function are exposed
  // without a prefix.
  const ArucoDetectorCtor = (cv['aruco_ArucoDetector'] ?? cv['ArucoDetector']) as
    | (new (d: unknown, p: unknown, r: unknown) => { detectMarkers: (m: unknown, c: unknown, i: unknown, r: unknown) => void; delete?: () => void })
    | undefined;
  if (!ArucoDetectorCtor) {
    const arucoKeys = Object.keys(cv).filter((k) => /aruco|Aruco/.test(k)).slice(0, 8);
    const msg = `cv.aruco_ArucoDetector not exposed at runtime (aruco-like keys: ${arucoKeys.join(', ') || 'none'})`;
    console.warn('[fiducialDecoder]', msg);
    setStatus({ state: 'error', message: msg });
    return { kind: 'none' };
  }
  const ParamsCtor = (cv['aruco_DetectorParameters'] ?? cv['DetectorParameters']) as
    | (new () => unknown)
    | undefined;
  // OpenCV 5.0's ArucoDetector(Dictionary, DetectorParameters, RefineParameters)
  // requires all three — no defaults in the JS binding. The JS binding for
  // RefineParameters likewise requires all three of its constructor args
  // explicitly (C++ defaults are 3.0f, 10.0f, true).
  const RefineParamsCtor = (cv['aruco_RefineParameters'] ?? cv['RefineParameters']) as
    | (new (errorCorrectionRate: number, minRepDistance: number, checkAllOrders: boolean) => unknown)
    | undefined;
  const DictFn = (cv['getPredefinedDictionary'] ?? cv['aruco_getPredefinedDictionary']) as
    | ((d: number) => unknown)
    | undefined;
  if (!ParamsCtor || !DictFn || !RefineParamsCtor) {
    const msg = `aruco helpers missing: DetectorParameters=${!!ParamsCtor}, RefineParameters=${!!RefineParamsCtor}, getPredefinedDictionary=${!!DictFn}`;
    console.warn('[fiducialDecoder]', msg);
    setStatus({ state: 'error', message: msg });
    return { kind: 'none' };
  }
  const MatVectorCtor = cv['MatVector'] as (new () => MatLike) | undefined;
  if (!MatVectorCtor) {
    const msg = 'cv.MatVector missing — cannot allocate corners output';
    console.warn('[fiducialDecoder]', msg);
    setStatus({ state: 'error', message: msg });
    return { kind: 'none' };
  }

  const src = cv.matFromImageData(imageData) as MatLike;
  const corners = new MatVectorCtor();
  const ids = new (cv.Mat as new () => MatLike)();
  const rejected = new MatVectorCtor();
  try {
    const cvKey = FORMAT_CV_KEY[format];
    const dictValue = (cv[cvKey] as number | undefined) ?? FORMAT_DICT_INT[format];
    let dictionary: unknown;
    let params: unknown;
    let refineParams: unknown;
    try {
      dictionary = DictFn(dictValue);
    } catch (e) {
      throw new Error(`getPredefinedDictionary(${dictValue}) threw: ${e instanceof Error ? e.message : String(e)}`);
    }
    try {
      params = new ParamsCtor();
    } catch (e) {
      throw new Error(`new aruco_DetectorParameters() threw: ${e instanceof Error ? e.message : String(e)}`);
    }
    try {
      // C++ defaults for RefineParameters — JS binding requires them explicitly.
      refineParams = new RefineParamsCtor(3.0, 10.0, true);
    } catch (e) {
      throw new Error(`new aruco_RefineParameters(3.0, 10.0, true) threw: ${e instanceof Error ? e.message : String(e)}`);
    }
    const dictType = typeof dictionary;
    const paramsType = typeof params;
    const refineType = typeof refineParams;
    if (dictionary == null || params == null || refineParams == null) {
      throw new Error(`null/undefined arg(s): dict=${dictType}, params=${paramsType}, refine=${refineType}`);
    }
    const detector = new ArucoDetectorCtor(dictionary, params, refineParams);
    try {
      detector.detectMarkers(src, corners, ids, rejected);
      const symbols = collectSymbols(corners, ids, format);
      setStatus({ state: 'ready' });
      return symbols.length === 0
        ? { kind: 'none' }
        : { kind: 'symbols', symbols };
    } finally {
      detector.delete?.();
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('[fiducialDecoder] detect threw:', msg);
    setStatus({ state: 'error', message: `detect threw: ${msg}` });
    return { kind: 'none' };
  } finally {
    src.delete?.();
    corners.delete?.();
    ids.delete?.();
    rejected.delete?.();
  }
}

interface MatLike {
  rows: number;
  cols: number;
  data32S?: Int32Array;
  // MatVector accessor
  get?: (i: number) => MatLike;
  // 2D point accessor for corner Mats — data32F holds 8 floats per marker
  // (4 corners × 2 coords) in row-major.
  data32F?: Float32Array;
  delete?: () => void;
}

function collectSymbols(corners: MatLike, ids: MatLike, format: FiducialFormat): DecodedSymbol[] {
  const count = ids.rows;
  if (count === 0 || !ids.data32S || !corners.get) return [];
  const kind: DecodedSymbolKind = format.startsWith('apriltag') ? 'apriltag' : 'aruco';
  const out: DecodedSymbol[] = [];
  for (let i = 0; i < count; i++) {
    const id = ids.data32S[i];
    const cornerMat = corners.get(i);
    const pts = cornerMat?.data32F;
    if (!pts || pts.length < 8) {
      cornerMat?.delete?.();
      continue;
    }
    const xs = [pts[0], pts[2], pts[4], pts[6]];
    const ys = [pts[1], pts[3], pts[5], pts[7]];
    const bbox: BinBbox = {
      x1: Math.min(...xs),
      y1: Math.min(...ys),
      x2: Math.max(...xs),
      y2: Math.max(...ys),
    };
    out.push({
      text: String(id),
      bbox,
      orientation: 0,
      kind,
      dictionary: format,
    });
    cornerMat?.delete?.();
  }
  return out;
}
